/**
 * The register's "More filters" panel - the grid that opens under the toolbar.
 *
 * Moved out of `AriregisterFilters.tsx` unchanged when that file reached the
 * size limit in AGENTS.md §4. The toolbar and this grid were already two
 * pieces: the toolbar is always on screen, the grid only while the operator has
 * opened it, and neither reads anything the other computes. Like its parent it
 * holds no state - the committed filter and the typing draft both live in the
 * list.
 *
 * The number fields stay native `<input type="number">` in `crm-inp` pills
 * rather than becoming anything cleverer: they are ranges an operator types, and
 * the browser's own stepper and numeric keypad are worth keeping.
 */
import { DateRangePicker, type DateRangeValue } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { LEGAL_FORMS } from "../../../lib/ariregister";
import type { AudienceDraft, AudienceFilter } from "../../../lib/audienceFilter";
import { FilterField, LicenceSinceField, RangeField } from "./filterFields";

export function AdvancedFilters({
  filters,
  draft,
  setDraft,
  patch,
  regRange,
  onRegRange,
}: {
  filters: AudienceFilter;
  draft: AudienceDraft;
  setDraft: (fn: (d: AudienceDraft) => AudienceDraft) => void;
  patch: (p: Partial<AudienceFilter>) => void;
  regRange: DateRangeValue | null;
  onRegRange: (r: DateRangeValue | null) => void;
}) {
  const t = useT();

  return (
    <div className="crm-card mb-3.5 grid grid-cols-1 gap-x-4 gap-y-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.legalForm")}</span>
        <FilterField
          className="w-full"
          value={filters.legalForm}
          onChange={(v) => patch({ legalForm: v })}
          label={t("ariregister.filter.legalForm")}
          anyLabel={t("ariregister.filter.legalFormAny")}
          options={LEGAL_FORMS.map((f) => ({ v: f, label: f }))}
        />
      </div>

      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.vat")}</span>
        <FilterField
          className="w-full"
          value={filters.hasVat}
          onChange={(v) => patch({ hasVat: v })}
          label={t("ariregister.filter.vat")}
          anyLabel={t("ariregister.filter.vatAny")}
          options={[
            { v: "true", label: t("ariregister.filter.vatYes") },
            { v: "false", label: t("ariregister.filter.vatNo") },
          ]}
        />
      </div>

      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.website")}</span>
        <FilterField
          className="w-full"
          value={filters.hasWebsite}
          onChange={(v) => patch({ hasWebsite: v })}
          label={t("ariregister.filter.website")}
          anyLabel={t("ariregister.filter.websiteAny")}
          options={[
            { v: "true", label: t("ariregister.filter.websiteYes") },
            { v: "false", label: t("ariregister.filter.websiteNo") },
          ]}
        />
      </div>

      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.registered")}</span>
        {/* `justify-between` is deliberately gone: it pushed the label to
            the far right of the field while every other control in this
            grid reads left to right from its own icon. The rest of the
            skin is retuned in index.css, because the shared picker renders
            a `Button` whose own utilities outrank a class handed in here. */}
        <DateRangePicker
          value={regRange}
          onChange={onRegRange}
          locale="et-EE"
          placeholder={t("ariregister.filter.registeredAny")}
          className="crm-fld crm-fld-date w-full"
        />
      </div>

      <RangeField
        label={t("ariregister.filter.capital")}
        min={draft.capitalMin}
        max={draft.capitalMax}
        onMin={(v) => setDraft((d) => ({ ...d, capitalMin: v }))}
        onMax={(v) => setDraft((d) => ({ ...d, capitalMax: v }))}
        minPlaceholder={t("ariregister.filter.capitalMin")}
        maxPlaceholder={t("ariregister.filter.capitalMax")}
        unit="EUR"
      />

      {/* Wealth: revenue (tax office) and employees (tax office). */}
      <RangeField
        label={t("ariregister.filter.revenue")}
        min={draft.revenueMin}
        max={draft.revenueMax}
        onMin={(v) => setDraft((d) => ({ ...d, revenueMin: v }))}
        onMax={(v) => setDraft((d) => ({ ...d, revenueMax: v }))}
        minPlaceholder={t("ariregister.filter.revenueMin")}
        maxPlaceholder={t("ariregister.filter.revenueMax")}
        unit="EUR"
      />

      <RangeField
        label={t("ariregister.filter.employees")}
        min={draft.employeesMin}
        max={draft.employeesMax}
        onMin={(v) => setDraft((d) => ({ ...d, employeesMin: v }))}
        onMax={(v) => setDraft((d) => ({ ...d, employeesMax: v }))}
        minPlaceholder={t("ariregister.filter.min")}
        maxPlaceholder={t("ariregister.filter.max")}
      />

      {/* The prospecting filter: "show me only the ones worth calling". Both
          bounds skip rows the pass has never scored, which is why the panel
          above the list says how fresh the column is. */}
      <RangeField
        label={t("ariregister.filter.webNeed")}
        min={draft.webNeedMin}
        max={draft.webNeedMax}
        onMin={(v) => setDraft((d) => ({ ...d, webNeedMin: v }))}
        onMax={(v) => setDraft((d) => ({ ...d, webNeedMax: v }))}
        minPlaceholder={t("ariregister.filter.min")}
        maxPlaceholder={t("ariregister.filter.max")}
      />

      {/* Arrears. The select and the floor sit in one cell because they are one
          question: "is this company in trouble with the tax office, and how
          much". The floor works on its own - a minimum implies a debt - so
          neither control has to be reached through the other. */}
      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.debt")}</span>
        <div className="flex min-w-0 items-center gap-1.5">
          <FilterField
            className="min-w-0 flex-1"
            value={filters.hasDebt}
            onChange={(v) => patch({ hasDebt: v })}
            label={t("ariregister.filter.debt")}
            anyLabel={t("ariregister.filter.debtAny")}
            options={[
              { v: "true", label: t("ariregister.filter.hasDebtYes") },
              { v: "false", label: t("ariregister.filter.hasDebtNo") },
            ]}
          />
          <label className="crm-inp w-[7.5rem] min-w-0 shrink-0">
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={draft.debtMin}
              onChange={(e) => setDraft((d) => ({ ...d, debtMin: e.target.value }))}
              placeholder={t("ariregister.filter.debtMin")}
            />
          </label>
        </div>
      </div>

      {/* Activity licences. The select and the "granted since" date are one
          question again: does this company hold a permit, and did it get one
          recently enough to still be setting itself up. */}
      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.licence")}</span>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <FilterField
            className="min-w-0 flex-1"
            value={filters.hasLicence}
            onChange={(v) => patch({ hasLicence: v })}
            label={t("ariregister.filter.licence")}
            anyLabel={t("ariregister.filter.licenceAny")}
            options={[
              { v: "true", label: t("ariregister.filter.hasLicenceYes") },
              { v: "false", label: t("ariregister.filter.hasLicenceNo") },
            ]}
          />
          <LicenceSinceField
            value={filters.licenceSince}
            onChange={(licenceSince) => patch({ licenceSince })}
            label={t("ariregister.filter.licenceSince")}
          />
        </div>
      </div>

      {/* Public procurement wins. The same pair as the licence cell: has the
          company won a public contract, and how recently. The day picker is
          the licence cell's, which is a plain "since" field. */}
      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.procurement")}</span>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <FilterField
            className="min-w-0 flex-1"
            value={filters.wonProcurement}
            onChange={(v) => patch({ wonProcurement: v })}
            label={t("ariregister.filter.procurement")}
            anyLabel={t("ariregister.filter.procurementAny")}
            options={[
              { v: "true", label: t("ariregister.filter.wonProcurementYes") },
              { v: "false", label: t("ariregister.filter.wonProcurementNo") },
            ]}
          />
          <LicenceSinceField
            value={filters.procurementSince}
            onChange={(procurementSince) => patch({ procurementSince })}
            label={t("ariregister.filter.procurementSince")}
          />
        </div>
      </div>

      <div className="min-w-0">
        <span className="crm-lab">{t("ariregister.filter.profitable")}</span>
        <FilterField
          className="w-full"
          value={filters.profitable}
          onChange={(v) => patch({ profitable: v })}
          label={t("ariregister.filter.profitable")}
          anyLabel={t("ariregister.filter.profitableAny")}
          options={[
            { v: "true", label: t("ariregister.filter.profitableYes") },
            { v: "false", label: t("ariregister.filter.profitableNo") },
          ]}
        />
      </div>

      <div className="min-w-0 sm:col-span-2 lg:col-span-1">
        <span className="crm-lab">{t("ariregister.filter.contact")}</span>
        {/* Two switched-on filters rather than three-state ones: "has an
            e-mail" narrows the list, "has no e-mail" is what the website
            filter above is for. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            aria-pressed={filters.hasEmail}
            onClick={() => patch({ hasEmail: !filters.hasEmail })}
            className={`crm-cb crm-cb-wide crm-cb-sm${filters.hasEmail ? " is-on" : ""}`}
          >
            {t("ariregister.filter.hasEmail")}
          </button>
          <button
            type="button"
            aria-pressed={filters.hasPhone}
            onClick={() => patch({ hasPhone: !filters.hasPhone })}
            className={`crm-cb crm-cb-wide crm-cb-sm${filters.hasPhone ? " is-on" : ""}`}
          >
            {t("ariregister.filter.hasPhone")}
          </button>
        </div>
      </div>
    </div>
  );
}
